import { yupResolver } from '@hookform/resolvers/yup';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    TextField,
    Typography,
    useTheme
} from '@mui/material';
import { ClearIcon, DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { UserRole } from 'Application/models';
import { useAgencyStore } from 'Application/store';
import { getAgencyBranchFromStorage, getUserRoleNameFromStorage, isEndDateBeforeStartDate } from 'Application/utils';
import { ThemeMode } from 'config';
import dayjs from 'dayjs';
import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as Yup from 'yup';

interface AddPerksDataProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    agencyName: string;
    pageNumber: number;
    pageSize: number;
}

const validationSchema = Yup.object().shape({
    svNumber: Yup.string().required('SV number is required.'),
    numOfTravellers: Yup.number().typeError('Please enter a valid number of travellers').required('No. of travellers value is required.'),
    origin: Yup.string().required('Travelling from location is required.'),
    departureDate: Yup.string().required('Departure date is required.'),
    returnDate: Yup.string().required('Return date is required.'),
    balanceNote: Yup.string().notRequired(),
    branch: Yup.string().required('Branch name is required')
});

const AddSvData: FC<AddPerksDataProps> = ({ isOpen, setIsOpen, agencyName, pageNumber, pageSize }) => {
    const [inputtedDestination, setInputtedDestination] = useState<string>('');
    const [destinations, setDestinations] = useState<string[]>([]);

    const [submitButtonClicked, setSubmitButtonClicked] = useState<boolean>(false);

    const { palette } = useTheme();
    const modalTitleColor = palette.mode === ThemeMode.DARK ? '#A0AEC0' : '#4A5568';
    const textColor = palette.mode === ThemeMode.DARK ? '#718096' : '#000';

    const { agencyBranches, isLoading: isAddPerksDataLoading, addAgencyLocalTravelData } = useAgencyStore();

    const userBranch = getAgencyBranchFromStorage();
    const userRole = getUserRoleNameFromStorage();

    const initialValues = {
        svNumber: '',
        numOfTravellers: 1,
        origin: '',
        departureDate: '',
        returnDate: '',
        balanceNote: '',
        branch: userBranch ?? ''
    };

    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors }
    } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validationSchema)
    });

    // const currentDate = dayjs();

    const earliestAllowedDate = dayjs('02/01/2026'); // String parameter set to MM/DD/YYYY format.

    const handleClose = () => {
        setIsOpen(false);
    };

    const onSubmit = async (formData: any) => {
        if (destinations.length === 0) {
            return;
        }

        const values: typeof initialValues = formData;

        const isReturnDateBeforeDepartureDate = isEndDateBeforeStartDate(dayjs(values.departureDate), dayjs(values.returnDate));

        if (isReturnDateBeforeDepartureDate) {
            toast.warning('Return date cannot be before departure date');
            return;
        }

        try {
            await addAgencyLocalTravelData(
                {
                    svNumber: values.svNumber,
                    numOfTravellers: values.numOfTravellers,
                    departureDate: dayjs(values.departureDate).format('YYYY-MM-DD'),
                    returnDate: dayjs(values.returnDate).format('YYYY-MM-DD'),
                    origin: values.origin,
                    destination: destinations,
                    balanceNote: values.balanceNote === '' ? undefined : values.balanceNote,
                    branch: values.branch
                },
                agencyName,
                pageNumber,
                pageSize
            );
            toast.success('Local travel data added successfully');
            handleClose();
        } catch (error) {
            toast.error('Error occurred while adding local travel data');
        }
    };

    const handleAddDestination = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            setDestinations((prevDestinations) => [...prevDestinations, inputtedDestination.trim()]);
            setInputtedDestination('');
        }
    };

    const handleRemoveDestination = (destination: string) => {
        setDestinations((prevDestinations) => prevDestinations.filter((value) => value !== destination));
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    width: '70%',
                    maxWidth: '600px'
                }
            }}
        >
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: modalTitleColor }}>Add SV data</span>
                    <IconButton onClick={handleClose}>
                        <ClearIcon />
                    </IconButton>
                </div>
                <div style={{ marginTop: '0.5rem', color: '#718096', fontSize: '0.875rem' }}>Please fill in the following details</div>
            </DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2}>
                        <Grid item sm={6}>
                            <Box className="mb-4">
                                <Typography sx={{ color: textColor }} className="mb-2 text-[1.1em]">
                                    SV number
                                </Typography>
                                <Controller
                                    name="svNumber"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type="text"
                                            fullWidth
                                            error={!!errors.svNumber}
                                            helperText={errors.svNumber?.message}
                                            onChange={(e) => field.onChange(e)}
                                        />
                                    )}
                                />
                            </Box>
                            <Box className="mb-4">
                                <Typography sx={{ color: textColor }} className="mb-2 text-[1.1em]">
                                    Number of travellers
                                </Typography>
                                <Controller
                                    name="numOfTravellers"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            inputProps={{ min: 1 }}
                                            type="number"
                                            fullWidth
                                            error={!!errors.numOfTravellers}
                                            helperText={errors.numOfTravellers?.message}
                                            onChange={(e) => field.onChange(e)}
                                        />
                                    )}
                                />
                            </Box>
                            <Box className="mb-4">
                                <Typography sx={{ color: textColor }} className="mb-2 text-[1.1em]">
                                    Travelling from
                                </Typography>
                                <Controller
                                    name="origin"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type="text"
                                            fullWidth
                                            error={!!errors.origin}
                                            helperText={errors.origin?.message}
                                            onChange={(e) => field.onChange(e)}
                                        />
                                    )}
                                />
                            </Box>
                            <Box className="mb-4">
                                <Typography sx={{ color: textColor }} className="mb-2 text-[1.1em]">
                                    Travelling to
                                </Typography>

                                <Box className="flex flex-col gap-2">
                                    <TextField
                                        type="text"
                                        value={inputtedDestination}
                                        fullWidth
                                        onChange={(e) => setInputtedDestination(e.target.value)}
                                        onKeyDown={handleAddDestination}
                                        error={destinations.length === 0 && submitButtonClicked}
                                        helperText={
                                            destinations.length === 0 &&
                                            submitButtonClicked &&
                                            'At least one travelling to location is required.'
                                        }
                                    />
                                    {destinations.length > 0 && (
                                        <Box className="grid grid-cols-3 gap-2">
                                            {destinations.map((value) => {
                                                const destinationIndex = destinations.indexOf(value);

                                                return (
                                                    value && (
                                                        <Chip
                                                            key={destinationIndex}
                                                            label={value}
                                                            onDelete={() => handleRemoveDestination(value)}
                                                        />
                                                    )
                                                );
                                            })}
                                        </Box>
                                    )}
                                    {destinations.length === 0 && (
                                        <Typography sx={{ color: '#718096', fontSize: '0.75rem' }}>
                                            Type the destination and then press 'Enter'.
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item sm={6}>
                            <Box className="mb-4">
                                <Typography sx={{ color: textColor }} className="mb-2 text-[1.1em]">
                                    Departure date
                                </Typography>
                                <Controller
                                    name="departureDate"
                                    control={control}
                                    render={({ field: { value, ...field } }) => (
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                {...field}
                                                className="w-full"
                                                value={value ? dayjs(value) : null}
                                                format="DD-MM-YYYY"
                                                minDate={earliestAllowedDate}
                                                slotProps={{
                                                    textField: {
                                                        inputProps: {
                                                            readOnly: true
                                                        },
                                                        error: !!errors.departureDate,
                                                        helperText: errors.departureDate?.message
                                                    }
                                                }}
                                            />
                                        </LocalizationProvider>
                                    )}
                                />
                            </Box>
                            <Box className="mb-4">
                                <Typography sx={{ color: textColor }} className="mb-2 text-[1.1em]">
                                    Return date
                                </Typography>
                                <Controller
                                    name="returnDate"
                                    control={control}
                                    render={({ field: { value, ...field } }) => (
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                {...field}
                                                className="w-full"
                                                value={value ? dayjs(value) : null}
                                                format="DD-MM-YYYY"
                                                minDate={earliestAllowedDate}
                                                slotProps={{
                                                    textField: {
                                                        inputProps: {
                                                            readOnly: true
                                                        },
                                                        error: !!errors.returnDate,
                                                        helperText: errors.returnDate?.message
                                                    }
                                                }}
                                            />
                                        </LocalizationProvider>
                                    )}
                                />
                            </Box>
                            <Box className="mb-4">
                                <Typography sx={{ color: textColor }} className="mb-2 text-[1.1em]">
                                    Balance note
                                </Typography>
                                <Controller
                                    name="balanceNote"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type="text"
                                            fullWidth
                                            error={!!errors.balanceNote}
                                            helperText={errors.balanceNote?.message}
                                            onChange={(e) => field.onChange(e)}
                                        />
                                    )}
                                />
                            </Box>
                            <Box className="mb-4">
                                <Typography sx={{ color: textColor }} className="mb-2 text-[1.1em]">
                                    Branch
                                </Typography>
                                <Controller
                                    name="branch"
                                    control={control}
                                    render={({ field }) =>
                                        userRole === UserRole.ROLE_AGENT || userRole === UserRole.ROLE_BRANCH_MANAGER ? (
                                            <TextField type="text" fullWidth disabled value={userBranch} />
                                        ) : (
                                            <Autocomplete
                                                {...field}
                                                options={agencyBranches.map((branch) => branch.split(' ')[0])}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        error={!!errors.branch}
                                                        helperText={errors.branch?.message}
                                                    />
                                                )}
                                                onChange={(_event, value) => value && setValue('branch', value)}
                                            />
                                        )
                                    }
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                color: '#4A5568',
                                borderColor: '#4A5568',
                                backgroundColor: '#ffffff'
                            }}
                            disabled={isAddPerksDataLoading} // Disable button while loading
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            onClick={() => setSubmitButtonClicked(true)}
                            variant="outlined"
                            sx={{
                                backgroundColor: '#673ab7',
                                color: '#ffffff',
                                marginLeft: '1.5rem',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    color: '#673ab7',
                                    borderColor: '#673ab7'
                                },
                                '&.Mui-disabled': {
                                    color: '#ffffff',
                                    backgroundColor: '#98A2B3'
                                }
                            }}
                            disabled={isAddPerksDataLoading} // Disable button while loading
                        >
                            Submit
                        </Button>
                    </Box>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddSvData;
